import { useEffect, useMemo, useRef } from 'react'
import { useSearch } from '@tanstack/react-router'
import { getDiyAnswersKey } from '../utils/living-guide-storage'
import type { UrlPreset } from '../types'

interface UseUrlPresetsOptions {
  urlPresets?: Array<UrlPreset>
  templateSlug: string
  currentAnswers: Record<string, string>
  onApplyAnswers: (answers: Record<string, string>) => void
}

interface UseUrlPresetsResult {
  appliedPresets: Array<UrlPreset>
  titleSuffix: string | undefined
  metaDescriptionOverride: string | undefined
  canonicalOverride: string | undefined
}

/**
 * Hook to apply URL presets to Q&A answers.
 * 
 * URL presets allow SEO-friendly landing pages like:
 * - /diy/tile-floor?heated=true → pre-fills heated floor answers
 * - /diy/tile-floor?vinyl=true → pre-fills vinyl flooring answers
 * 
 * Priority (lowest wins):
 * 1. URL presets (only on initial page load)
 * 2. sessionStorage (persisted during session)
 * 3. localStorage (persisted across sessions)
 * 4. User-entered answers
 */
export function useUrlPresets({
  urlPresets,
  templateSlug,
  currentAnswers,
  onApplyAnswers,
}: UseUrlPresetsOptions): UseUrlPresetsResult {
  const search = useSearch({ strict: false })
  const hasAppliedRef = useRef(false)
  
  // Find matching presets based on URL params
  const appliedPresets = useMemo(() => {
    if (!urlPresets || urlPresets.length === 0) return []
    
    return urlPresets.filter(preset => {
      const paramValue = search[preset.urlParam]
      const expectedValue = preset.urlValue ?? 'true'
      return paramValue === expectedValue
    })
  }, [urlPresets, search])
  
  // Apply presets on initial load (only once)
  useEffect(() => {
    if (hasAppliedRef.current) return
    if (appliedPresets.length === 0) return
    
    // Check if user already has stored answers (don't override)
    const hasStoredAnswers = (() => {
      try {
        const sessionStored = sessionStorage.getItem(getDiyAnswersKey(templateSlug))
        if (sessionStored && Object.keys(JSON.parse(sessionStored)).length > 0) {
          return true
        }
        const localStored = localStorage.getItem(getDiyAnswersKey(templateSlug))
        if (localStored && Object.keys(JSON.parse(localStored)).length > 0) {
          return true
        }
      } catch {
        // Ignore parse errors
      }
      return false
    })()
    
    // Only apply presets if no existing answers
    if (hasStoredAnswers) {
      hasAppliedRef.current = true
      return
    }
    
    // Merge all preset answer mappings
    const presetAnswers: Record<string, string> = {}
    for (const preset of appliedPresets) {
      Object.assign(presetAnswers, preset.answerMapping)
    }
    
    // Apply if we have any preset answers
    if (Object.keys(presetAnswers).length > 0) {
      onApplyAnswers({ ...currentAnswers, ...presetAnswers })
    }
    
    hasAppliedRef.current = true
  }, [appliedPresets, templateSlug, currentAnswers, onApplyAnswers])
  
  // Extract SEO overrides from applied presets (first match wins)
  const titleSuffix = useMemo(() => {
    for (const preset of appliedPresets) {
      if (preset.titleSuffix) return preset.titleSuffix
    }
    return undefined
  }, [appliedPresets])
  
  const metaDescriptionOverride = useMemo(() => {
    for (const preset of appliedPresets) {
      if (preset.metaDescriptionOverride) return preset.metaDescriptionOverride
    }
    return undefined
  }, [appliedPresets])
  
  const canonicalOverride = useMemo(() => {
    for (const preset of appliedPresets) {
      if (preset.canonicalOverride) return preset.canonicalOverride
    }
    return undefined
  }, [appliedPresets])
  
  return {
    appliedPresets,
    titleSuffix,
    metaDescriptionOverride,
    canonicalOverride,
  }
}
